// Fleet page — observability surface for every known mesh node.
// Polls /api/nodes every 5s and renders one card per node so an admin (or the
// AI sysadmin agent) can see at a glance who's online, where they live, and
// when they last reported in.
//
// Health dot semantics (matches the Stat tone palette used elsewhere):
//   green  — last_event_at within the last 60s   (online)
//   yellow — last_event_at within the last 5min  (idle / late beacon)
//   red    — older or never-seen                 (offline)

import { api, shortHex, type FleetNode } from "../api";
import { ageLabel, ageMs, cached, fleetHealth } from "../observation";
import { useObservation } from "../observations";

const KIND_TONE: Record<string, string> = {
  cluster: "bg-mesh-accent/20 text-mesh-accent border-mesh-accent/40",
  mac: "bg-mesh-ok/20 text-mesh-ok border-mesh-ok/40",
  laptop: "bg-mesh-ok/20 text-mesh-ok border-mesh-ok/40",
  android: "bg-mesh-warn/20 text-mesh-warn border-mesh-warn/40",
  holo: "bg-purple-500/20 text-purple-300 border-purple-500/40",
  pi: "bg-pink-500/20 text-pink-300 border-pink-500/40",
  unknown: "bg-mesh-mute/20 text-mesh-mute border-mesh-mute/40",
};

function kindClass(kind: string): string {
  return KIND_TONE[kind] ?? KIND_TONE.unknown!;
}

function humanizeAgo(now: number, ms: number | null): string {
  if (ms == null) return "never";
  return ageLabel(now - ms);
}

export default function FleetPage() {
  const observation = useObservation(api.nodes);
  const data = observation.data;
  const err = observation.error;
  const loading = !data && !err;
  const isCached = cached(observation);
  const nodes: FleetNode[] = data?.nodes ?? [];
  const history: FleetNode[] = data?.history ?? [];
  const groups = [{ title: "Current fleet", nodes }, { title: "Network history", nodes: history }];
  const now = (data?.now ?? 0) + ageMs(observation);

  return (
    <div className="space-y-4">
      <div className="flex items-baseline gap-3 flex-wrap">
        <h1 className="text-xl font-bold text-mesh-accent">fleet</h1>
        <div className="text-sm text-mesh-mute">
          {data
            ? `${isCached ? "cached counts · " : ""}${data.online_count} online as reported · ${data.total} current · ${data.kind_count} kinds · ${data.historical_count ?? 0} historical`
            : loading ? "checking…" : "unavailable"}
        </div>
      </div>

      {data?.community && (
        <div className="min-w-0 space-y-1 text-sm text-mesh-mute" data-testid="fleet-community">
          <p>Network · <span className="font-mono break-all">{data.community.id}</span></p>
          <p>{data.community.source === "observed" ? "Observed indexed identity" : "Configured identity · not yet observed"}</p>
          {data.community.mismatch && (
            <p role="alert" className="text-mesh-warn">
              Configuration differs from observed network. Configured · <span className="font-mono break-all">{data.community.configured_id}</span>
            </p>
          )}
        </div>
      )}
      {data && (
        <p role="status" data-testid="fleet-freshness" className="text-sm text-mesh-mute">
          {isCached ? "stale · cached observations" : "fresh observations"} · refreshed {ageLabel(ageMs(observation))}
        </p>
      )}
      {err && (
        <div
          role="alert"
          className="bg-mesh-err/20 text-mesh-err p-3 rounded text-sm"
        >
          {err}
        </div>
      )}

      {!err && loading && nodes.length === 0 && (
        <div className="text-mesh-mute text-sm py-6 text-center">loading…</div>
      )}

      {!loading && !err && nodes.length === 0 && (
        <div className="bg-mesh-panel/40 rounded-lg p-8 text-center text-mesh-mute text-sm">
          No current nodes registered. Historical records do not count as current fleet.
        </div>
      )}

      {groups.filter(group => group.nodes.length > 0).map(group => (
        <section key={group.title} className="space-y-3" aria-label={group.title}>
          <h2 className="text-sm uppercase tracking-wider text-mesh-mute">{group.title}</h2>
          {group.title === "Network history" && <p className="text-sm text-mesh-mute">Preserved records from a previous network or explicitly decommissioned nodes. Excluded from current counts.</p>}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {group.nodes.map((n) => {
            const health = fleetHealth(now, n.last_event_at);
            const historical = n.membership === "previous-network" || n.membership === "retired";
            return (
              <div
                key={n.peer_id}
                className="min-w-0 bg-mesh-panel/60 rounded-lg p-4 flex flex-col gap-2 border border-mesh-panel"
              >
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-block w-2.5 h-2.5 rounded-full ${historical ? "bg-mesh-mute" : isCached ? "bg-mesh-warn" : health.cls}`}
                    aria-hidden="true"
                  />
                  <span
                    className={`text-xs uppercase tracking-wider px-2 py-0.5 rounded border ${kindClass(n.kind)}`}
                  >
                    {n.kind}
                  </span>
                  <span className="ml-auto text-xs text-mesh-mute">
                    {isCached ? "cached · " : ""}{historical ? "historical" : health.label} · {humanizeAgo(now, n.last_event_at)}
                  </span>
                </div>
                <p className="text-xs text-mesh-mute">
                  {n.membership === "previous-network" ? "Previous network" : n.membership === "retired" ? "Decommissioned" : n.membership === "unknown" ? "Network unknown" : "Current network"}
                  {n.community && <> · <span className="font-mono break-all">{n.community}</span></>}
                </p>
                <div className="text-base font-bold text-mesh-ink truncate">
                  {n.label ?? n.peer_id}
                </div>
                <div className="text-xs text-mesh-mute font-mono truncate">
                  {shortHex(n.peer_id, 12, 8)}
                </div>
                <div className="text-xs text-mesh-mute flex flex-col gap-0.5">
                  <span>
                    <span className="opacity-60">loc</span>{" "}
                    {n.location ?? <span className="opacity-50">—</span>}
                  </span>
                  <span>
                    <span className="opacity-60">owner</span>{" "}
                    {n.owner ?? <span className="opacity-50">—</span>}
                  </span>
                  <span>
                    <span className="opacity-60">health</span>{" "}
                    {humanizeAgo(now, n.last_health_at)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
        </section>
      ))}
    </div>
  );
}
